import { Type, type Static } from "@sinclair/typebox";

const Id = Type.String({ minLength: 1, maxLength: 100 });
const Name = Type.String({ minLength: 1, maxLength: 200 });
const DateOnly = Type.String({ format: "date" });
const NullableDate = Type.Union([DateOnly, Type.Null()]);
const NullableId = Type.Union([Id, Type.Null()]);
const Money = Type.Number({ minimum: 0, maximum: 100000000 });
const Rate = Type.Number({ minimum: 0, maximum: 100000 });
const NullableRate = Type.Union([Rate, Type.Null()]);
const NullableMoney = Type.Union([Money, Type.Null()]);
const Percent = Type.Number({ minimum: 0, maximum: 100 });
const Markup = Type.Number({ minimum: -100, maximum: 1000 });
const Year = Type.Integer({ minimum: 2000, maximum: 2200 });
const Role = Type.Union(
  (["worker", "technician", "administrative", "owner"] as const).map((value) =>
    Type.Literal(value),
  ),
);
const objectOptions = { additionalProperties: false } as const;
export const BusinessScenarioEmployeeSchema = Type.Object(
  {
    id: Id,
    sourceEmployeeId: NullableId,
    name: Name,
    role: Role,
    included: Type.Boolean(),
    teamId: NullableId,
    costMode: Type.Union([Type.Literal("hourly"), Type.Literal("annual")]),
    hourlyCost: NullableRate,
    annualCost: NullableMoney,
    annualHours: Type.Number({ minimum: 0, maximum: 8784 }),
    contractPercent: Type.Number({ exclusiveMinimum: 0, maximum: 100 }),
    startDate: NullableDate,
    directPercent: Percent,
  },
  objectOptions,
);
export const BusinessScenarioTeamSchema = Type.Object(
  { id: Id, name: Name },
  objectOptions,
);
export const BusinessStructureCostSchema = Type.Object(
  {
    id: Id,
    sourceCostCenterId: NullableId,
    name: Name,
    included: Type.Boolean(),
    mode: Type.Union([
      Type.Literal("fixed"),
      Type.Literal("perEmployee"),
      Type.Literal("perHour"),
    ]),
    amount: Money,
    // Optional: existing scenarios retain their entered amounts.
    amountConfirmed: Type.Optional(Type.Boolean()),
    startDate: NullableDate,
    teamId: NullableId,
    replacedBy: Type.Union(
      (["none", "personnel", "owner", "vehicle"] as const).map((value) =>
        Type.Literal(value),
      ),
    ),
  },
  objectOptions,
);
export const BusinessVehicleSchema = Type.Object(
  {
    id: Id,
    name: Name,
    included: Type.Boolean(),
    teamId: NullableId,
    replacesCostCenterId: NullableId,
    startDate: NullableDate,
    purchaseCost: Money,
    residualValue: Money,
    usefulLifeYears: Type.Number({ exclusiveMinimum: 0, maximum: 100 }),
    recurringAnnualCost: Money,
    financingAmount: Money,
    annualPrincipalRepayment: Money,
    annualInterest: Money,
  },
  objectOptions,
);
export const BusinessPricingSchema = Type.Object(
  {
    laborPriceMode: Type.Union([
      Type.Literal("averageMarkup"),
      Type.Literal("manual"),
    ]),
    laborHourlyPrice: Rate,
    laborMarkupPercent: Markup,
    materialsCostPerHour: Rate,
    materialsMarkupPercent: Markup,
    subcontractCostPerHour: Rate,
    subcontractMarkupPercent: Markup,
    soldHours: Type.Union([
      Type.Number({ minimum: 0, maximum: 5000000 }),
      Type.Null(),
    ]),
    acquiredRevenue: NullableMoney,
    targetMarginPercent: Type.Number({ minimum: 0, maximum: 99.99 }),
  },
  objectOptions,
);
export const BusinessScenarioInputSchema = Type.Object(
  {
    schemaVersion: Type.Literal(1),
    sourceYear: Year,
    targetYear: Year,
    asOf: DateOnly,
    employees: Type.Array(BusinessScenarioEmployeeSchema, { maxItems: 500 }),
    teams: Type.Array(BusinessScenarioTeamSchema, { maxItems: 100 }),
    structureCosts: Type.Array(BusinessStructureCostSchema, { maxItems: 500 }),
    vehicles: Type.Array(BusinessVehicleSchema, { maxItems: 100 }),
    pricing: BusinessPricingSchema,
  },
  objectOptions,
);
export type BusinessScenarioEmployee = Static<
  typeof BusinessScenarioEmployeeSchema
>;
export type BusinessScenarioTeam = Static<typeof BusinessScenarioTeamSchema>;
export type BusinessStructureCost = Static<typeof BusinessStructureCostSchema>;
export type BusinessVehicle = Static<typeof BusinessVehicleSchema>;
export type BusinessPricing = Static<typeof BusinessPricingSchema>;
export type BusinessScenarioInput = Static<typeof BusinessScenarioInputSchema>;

export const BusinessEmployeeSourceSchema = Type.Object(
  {
    id: Id,
    name: Name,
    role: Role,
    workedHours: Type.Number({ minimum: 0 }),
    projectedAnnualHours: Type.Number({ minimum: 0 }),
    directHourlyCost: NullableRate,
    allocatedStructureHourlyCost: NullableRate,
    sourceNote: Type.String({ maxLength: 4000 }),
  },
  objectOptions,
);
export type BusinessEmployeeSource = Static<
  typeof BusinessEmployeeSourceSchema
>;
export const BusinessSimulatorSourceSchema = Type.Object(
  {
    sourceYear: Year,
    targetYear: Year,
    asOf: DateOnly,
    generatedAt: Type.String({ format: "date-time" }),
    snapshotId: Id,
    baseline: BusinessScenarioInputSchema,
    employees: Type.Array(BusinessEmployeeSourceSchema, { maxItems: 500 }),
    warnings: Type.Array(Type.String({ maxLength: 4000 }), { maxItems: 100 }),
  },
  objectOptions,
);
export type BusinessSimulatorSource = Static<
  typeof BusinessSimulatorSourceSchema
>;
export const BusinessScenarioSaveRequestSchema = Type.Object(
  {
    operationId: Type.String({ format: "uuid" }),
    id: Type.Union([Type.String({ format: "uuid" }), Type.Null()]),
    version: Type.Union([Type.Integer({ minimum: 1 }), Type.Null()]),
    name: Name,
    input: BusinessScenarioInputSchema,
    sourceSnapshot: BusinessSimulatorSourceSchema,
  },
  objectOptions,
);
export type BusinessScenarioSaveRequest = Static<
  typeof BusinessScenarioSaveRequestSchema
>;
export interface SavedBusinessScenario {
  id: string;
  name: string;
  version: number;
  input: BusinessScenarioInput;
  sourceSnapshot: BusinessSimulatorSource;
  createdAt: string;
  updatedAt: string;
}
export type BusinessScenarioSaved = SavedBusinessScenario;
export interface BusinessEmployeeResult {
  id: string;
  name: string;
  role: BusinessScenarioEmployee["role"];
  teamId: string | null;
  yearFraction: number;
  paidHours: number;
  operationalHours: number;
  annualCost: number;
  directCost: number;
  indirectCost: number;
}
export interface BusinessTeamResult {
  id: string;
  name: string;
  employeeIds: string[];
  operationalHours: number;
  soldHours: number;
  laborCost: number;
  laborRevenue: number;
  materialsCost: number;
  subcontractCost: number;
  totalRevenue: number;
  marginBeforeStructure: number;
  structureAllocation: number;
  operatingProfit: number;
}
export interface BusinessScenarioResult {
  workerCount: number;
  employeeCount: number;
  fullTimeEquivalent: number;
  teamCount: number;
  capacityHours: number;
  soldHours: number;
  averageWorkerHourlyCost: number | null;
  laborHourlyPrice: number | null;
  laborRevenue: number;
  materialsRevenue: number;
  subcontractRevenue: number;
  totalRevenue: number;
  workerCost: number;
  directLaborCost: number;
  indirectPersonnelCost: number;
  ownerCost: number;
  materialsCost: number;
  subcontractCost: number;
  otherStructureCost: number;
  vehicleEconomicCost: number;
  structureCost: number;
  structureHourlyCost: number | null;
  marginBeforeStructure: number;
  operatingProfit: number;
  operatingMarginPercent: number | null;
  interestCost: number;
  preTaxResult: number;
  fixedCosts: number;
  variableCosts: number;
  contributionMarginRate: number | null;
  breakEvenRevenue: number | null;
  targetRevenue: number | null;
  targetHours: number | null;
  capacityExceeded: boolean;
  targetRevenueGap: number | null;
  revenuePerTeam: number | null;
  capacityUtilizationRequiredPercent: number | null;
  fundingNeedRevenue: number | null;
  commercialGap: number | null;
  acquiredRevenue: number | null;
  employees: BusinessEmployeeResult[];
  teams: BusinessTeamResult[];
  cash: {
    capitalExpenditure: number;
    financingInflow: number;
    principalRepayment: number;
    interestPayment: number;
    estimatedNetBeforeWorkingCapital: number;
    complete: false;
    note: string;
  };
  warnings: string[];
}
export interface BusinessScenarioComparison {
  baseline: BusinessScenarioResult;
  simulated: BusinessScenarioResult;
  delta: {
    capacityHours: number;
    totalRevenue: number;
    structureCost: number;
    operatingProfit: number;
    structureHourlyCost: number | null;
  };
}
